import React, { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import {
	Modal,
	Button,
	Box,
	Typography,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Alert,
} from "@mui/material";
import FormConfig from "../Config/FormConfig";
import Swal from "sweetalert2";
import { obtenerDatosSites } from "../../api/config";
import { GridContract } from "../Config/GridContract";
import { GlobalContext } from "../../GlobalContext";
import { validateField } from "../../Labels/descriptions/LabelsDescriptionsRestrictions/Restrictions";

const ModalCreateContract = ({ open, onClose, handleDataCrearContrato }) => {
	const { site_id } = useContext(GlobalContext);
	const { t } = useTranslation();

	const contratoExtraLabel = t("lab_tool.contract.labels.contrato_extra");
	const mesesContratoLabel = t("lab_tool.contract.labels.meses_contrato");

	const [paso, setPaso] = useState(1);
	const [changedFields, setChangedFields] = useState({
		[contratoExtraLabel]: "false",
		[mesesContratoLabel]: "",
	});
	const [errors, setErrors] = useState({});
	const [formError, setFormError] = useState("");

	// grid data
	const [dataDay, setDataDay] = useState([]);
	const [dataPeriod, setDataPeriod] = useState([]);
	const [allDaysEmpty, setAllDaysEmpty] = useState(false);
	const [hasIncompletePeriods, setHasIncompletePeriods] = useState(false);

	const [tipoContratos, setTipoContratos] = useState([]);
	const [tipoContratoSeleccionado, setTipoContratoSeleccionado] = useState("");
	const [errorSelectTipoContrato, setErrorSelectTipoContrato] = useState(false);

	const tooltips = [
		t("lab_tool.contract.tooltips.nombre"),
		t("lab_tool.contract.tooltips.kronos_name"),
		t("lab_tool.contract.tooltips.productividad"),
		t("lab_tool.contract.tooltips.rotacion"),
		t("lab_tool.contract.tooltips.ausentismo"),
		t("lab_tool.contract.tooltips.cotsup"),
		t("lab_tool.contract.tooltips.cotinf"),
		t("lab_tool.contract.tooltips.dias_lab_maximos"),
		t("lab_tool.contract.tooltips.dias_lab_minimos"),
		t("lab_tool.contract.tooltips.contrato"),
		t("lab_tool.contract.tooltips.despido"),
		t("lab_tool.contract.tooltips.salario"),
		t("lab_tool.contract.tooltips.zcero"),
		t("lab_tool.contract.tooltips.hora_contrato"),
		t("lab_tool.contract.tooltips.contrato_extra"),
		t("lab_tool.contract.tooltips.meses_contrato"),
	];

	const labels = [
		t("lab_tool.contract.labels.nombre"),
		t("lab_tool.contract.labels.kronos_name"),
		t("lab_tool.contract.labels.productividad"),
		t("lab_tool.contract.labels.rotacion"),
		t("lab_tool.contract.labels.ausentismo"),
		t("lab_tool.contract.labels.cotsup"),
		t("lab_tool.contract.labels.cotinf"),
		t("lab_tool.contract.labels.dias_lab_maximos"),
		t("lab_tool.contract.labels.dias_lab_minimos"),
		t("lab_tool.contract.labels.contrato"),
		t("lab_tool.contract.labels.despido"),
		t("lab_tool.contract.labels.salario"),
		t("lab_tool.contract.labels.zcero"),
		t("lab_tool.contract.labels.hora_contrato"),
		t("lab_tool.contract.labels.contrato_extra"),
		t("lab_tool.contract.labels.meses_contrato"),
	];

	const fieldConfigs = {
		[t("lab_tool.contract.labels.nombre")]: {
			minLength: 3,
			regex: /^(?=.*[A-Za-zÁÉÍÓÚáéíóú])[A-Za-zÁÉÍÓÚáéíóú0-9\s]+$/,
		},
		[t("lab_tool.contract.labels.kronos_name")]: {
			minLength: 3,
			regex: /^(?=.*[A-Za-zÁÉÍÓÚáéíóú])[A-Za-zÁÉÍÓÚáéíóú0-9\s]+$/,
		},
		[t("lab_tool.contract.labels.productividad")]: {
			type: "number",
			allowDecimal: true,
		},
		[t("lab_tool.contract.labels.rotacion")]: {
			type: "number",
			allowDecimal: true,
		},
		[t("lab_tool.contract.labels.zcero")]: {
			type: "number",
			allowDecimal: false,
		},
		[t("lab_tool.contract.labels.cotsup")]: {
			type: "number",
			allowDecimal: true,
		},
		[t("lab_tool.contract.labels.cotinf")]: {
			type: "number",
			allowDecimal: true,
		},
		[t("lab_tool.contract.labels.ausentismo")]: {
			type: "number",
			allowDecimal: true,
		},
		[t("lab_tool.contract.labels.contrato")]: {
			type: "number",
			allowDecimal: true,
		},
		[t("lab_tool.contract.labels.despido")]: {
			type: "number",
			allowDecimal: true,
		},
		[t("lab_tool.contract.labels.salario")]: {
			type: "number",
			allowDecimal: true,
		},
		[t("lab_tool.contract.labels.hora_contrato")]: {
			type: "number",
			allowDecimal: true,
		},
		[t("lab_tool.contract.labels.dias_lab_maximos")]: {
			type: "number",
			allowDecimal: false,
		},
		[t("lab_tool.contract.labels.dias_lab_minimos")]: {
			type: "number",
			allowDecimal: false,
		},
		[t("lab_tool.contract.labels.contrato_extra")]: {
			type: "checkbox",
		},
		[t("lab_tool.contract.labels.meses_contrato")]: {
			allowDecimal: false,
			type: "text",
			dependsOn: t("lab_tool.contract.labels.contrato_extra"),
			dependsValue: "true",
		},
	};
	// load contract types
	useEffect(() => {
		async function loadTipos() {
			try {
				const resp = await obtenerDatosSites(`/tipo_contrato/get?site_id=${site_id}`);
				setTipoContratos(resp);
			} catch (err) {
				console.error(err);
			}
		}
		loadTipos();
	}, [site_id]);

	// Cambio de campo general
	const handleFieldChange = (field, value) => {
		const updated = { ...changedFields, [field]: value };
		if (field === contratoExtraLabel && value === "false") {
			updated[mesesContratoLabel] = null; // mismo comportamiento que Create
		}
		setChangedFields(updated);
		// no real-time validation for checkbox and its dependent
		if (field === contratoExtraLabel || field === mesesContratoLabel) {
			setErrors((e) => ({ ...e, [field]: "" }));
		} else {
			setErrors((e) => ({ ...e, [field]: validateField(field, value, updated, t) }));
		}
	};

	const handleNextStep = () => {
		const required = [
			t("lab_tool.contract.labels.nombre"),
			t("lab_tool.contract.labels.kronos_name"),
			t("lab_tool.contract.labels.productividad"),
			t("lab_tool.contract.labels.rotacion"),
			t("lab_tool.contract.labels.ausentismo"),
			t("lab_tool.contract.labels.cotsup"),
			t("lab_tool.contract.labels.cotinf"),
			t("lab_tool.contract.labels.dias_lab_maximos"),
			t("lab_tool.contract.labels.dias_lab_minimos"),
			t("lab_tool.contract.labels.contrato"),
			t("lab_tool.contract.labels.despido"),
			t("lab_tool.contract.labels.salario"),
			t("lab_tool.contract.labels.zcero"),
			t("lab_tool.contract.labels.hora_contrato"),
		];
		if (changedFields[contratoExtraLabel] === "true") required.push(mesesContratoLabel);

		const newErr = {};
		required.forEach((lbl) => {
			newErr[lbl] = validateField(lbl, changedFields[lbl], changedFields, t);
		});
		setErrors(newErr);

		if (Object.values(newErr).some((msg) => msg)) {
			setFormError(t("aux.messages.fix_error"));
		} else {
			setFormError("");
			setPaso(2);
		}
	};

	// receive grid updates
	const handleDataUpdate = (daysArr, periodsArr, allEmpty) => {
		setDataDay(daysArr);
		setDataPeriod(periodsArr);
		setAllDaysEmpty(allEmpty);
		setHasIncompletePeriods(
			periodsArr.some((d) => d.periods.some((p) => !p.start_time || !p.end_time))
		);
	};

	const handleCreate = () => {
		setFormError("");
		if (allDaysEmpty) return setFormError(t("aux.messages.select_day"));
		if (hasIncompletePeriods) return setFormError(t("aux.messages.alert.grid_d_p.pairs_error"));
		if (!tipoContratoSeleccionado) return setErrorSelectTipoContrato(true);

		const payload = {
			id: 0,
			config_site_id: site_id,
			nombre: changedFields[t("lab_tool.contract.labels.nombre")],
			kronos_name: changedFields[t("lab_tool.contract.labels.kronos_name")],
			productividad: changedFields[t("lab_tool.contract.labels.productividad")],
			rotacion: changedFields[t("lab_tool.contract.labels.rotacion")],
			ausentismo: changedFields[t("lab_tool.contract.labels.ausentismo")],
			cotsup: changedFields[t("lab_tool.contract.labels.cotsup")],
			cotinf: changedFields[t("lab_tool.contract.labels.cotinf")],
			dias_lab_maximos: changedFields[t("lab_tool.contract.labels.dias_lab_maximos")],
			dias_lab_minimos: changedFields[t("lab_tool.contract.labels.dias_lab_minimos")],
			contrato: changedFields[t("lab_tool.contract.labels.contrato")],
			despido: changedFields[t("lab_tool.contract.labels.despido")],
			salario: changedFields[t("lab_tool.contract.labels.salario")],
			zcero: changedFields[t("lab_tool.contract.labels.zcero")],
			hora_contrato: changedFields[t("lab_tool.contract.labels.hora_contrato")],
			contrato_extra: changedFields[contratoExtraLabel],
			meses_contrato: changedFields[mesesContratoLabel],
			id_tipo_contrato: tipoContratoSeleccionado,
			contracts_per_day: dataDay,
			contracts_per_periods: dataPeriod,
		};

		handleDataCrearContrato(payload);
		Swal.fire({
			title: t("aux.messages.alert.success"),
			text: t("aux.messages.alert.text_alert.create_contract"),
			icon: "success",
		});
		onClose();
		// reset form
		setPaso(1);
		setChangedFields({ [contratoExtraLabel]: "false", [mesesContratoLabel]: "0" });
		setErrors({});
	};

	return (
		<Modal
			open={open}
			onClose={onClose}
			sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
		>
			<Box
				sx={{
					width: { xs: "95%", sm: "80%", md: "60%" },
					maxHeight: "90vh",
					bgcolor: "background.paper",
					p: 2,
					overflowY: "auto",
				}}
			>
				{paso === 1 && (
					<>
						<Typography variant="h6" gutterBottom>
							{t("aux.step")} 1 {t("aux.of")} 2 – {t("config.config_general")}
						</Typography>

						<FormControl fullWidth required error={errorSelectTipoContrato} sx={{ mb: 2 }}>
							<InputLabel>{t("aux.type_contract")}</InputLabel>
							<Select
								value={tipoContratoSeleccionado}
								onChange={(e) => {
									setTipoContratoSeleccionado(e.target.value);
									setErrorSelectTipoContrato(false);
								}}
							>
								<MenuItem value="" disabled>
									<em>{t("aux.select_contract_placeholder")}</em>
								</MenuItem>
								{tipoContratos.map((tc) => (
									<MenuItem key={tc.id} value={tc.id}>
										{tc.nombre}
									</MenuItem>
								))}
							</Select>
						</FormControl>

						<FormConfig
							labels={labels}
							valorDefault={changedFields}
							handleFieldChange={handleFieldChange}
							tooltips={tooltips}
							errors={errors}
							fieldConfigs={fieldConfigs}
						/>
						{formError && (
							<Alert severity="error" sx={{ mt: 2 }}>
								{formError}
							</Alert>
						)}

						<Button fullWidth variant="contained" onClick={handleNextStep} sx={{ mt: 2 }}>
							{t("aux.buttons.continue")}
						</Button>
					</>
				)}

				{paso === 2 && (
					<>
						<Typography variant="h6" gutterBottom>
							{t("aux.step")} 2 {t("aux.of")} 2 – {t("config.config_day_period")}
						</Typography>

						<GridContract onDataUpdate={handleDataUpdate} showError={allDaysEmpty} />
						{allDaysEmpty && (
							<Alert severity="error" sx={{ mt: 2 }}>
								{t("aux.messages.select_day")}
							</Alert>
						)}
						{!allDaysEmpty && hasIncompletePeriods && (
							<Alert severity="error" sx={{ mt: 2 }}>
								{t("aux.messages.alert.grid_d_p.pairs_error")}
							</Alert>
						)}

						<Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
							<Button variant="outlined" onClick={() => setPaso(1)}>
								{t("aux.buttons.back")}
							</Button>
							<Button
								variant="contained"
								onClick={handleCreate}
								disabled={allDaysEmpty || hasIncompletePeriods}
							>
								{t("aux.buttons.config.create")}
							</Button>
						</Box>
					</>
				)}
			</Box>
		</Modal>
	);
};

export { ModalCreateContract };
